import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import { getCategories, adminCreateListing, type Category } from "@/utils/adminApi";
import request from "@/utils/api";
import DraggableImageGrid from "@/components/ui/DraggableImageGrid";

export default function AdminPublishPage() {
  const navigate = useNavigate();
  const contentRef = useRef<HTMLTextAreaElement>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    companyName: "",
    contactName: "",
    contactPhone: "",
    contactWechat: "",
    status: "published",
  });
  const [images, setImages] = useState<string[]>([]);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);

  // 加载分类数据
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await getCategories();
        const activeCategories = data.filter((cat: Category) => cat.status === 'active');
        setCategories(activeCategories);
      } catch (err) {
        console.error("加载分类失败:", err);
      } finally {
        setIsLoadingCategories(false);
      }
    };
    loadCategories();
  }, []);

  // 处理分类标签选择
  const toggleCategory = (categoryId: string) => {
    setSelectedCategoryIds((prev) => {
      if (prev.includes(categoryId)) {
        return prev.filter((id) => id !== categoryId);
      }
      if (prev.length >= 5) {
        setError("最多只能选择5个分类");
        setTimeout(() => setError(""), 3000);
        return prev;
      }
      return [...prev, categoryId];
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 上传图片到服务器
  const uploadImage = async (base64Data: string): Promise<string> => {
    try {
      // 将 base64 转换为文件
      const response = await fetch(base64Data);
      const blob = await response.blob();
      const ext = blob.type.split('/')[1] || 'png';
      const filename = `image_${Date.now()}.${ext}`;
      
      // 使用 FormData 上传
      const formData = new FormData();
      formData.append('file', blob, filename);
      
      const result = await request('/upload', {
        method: 'POST',
        body: formData,
      });
      
      // 返回上传后的 URL
      if (result && (result as any).url) {
        return (result as any).url;
      }
      // 兼容其他返回格式
      if (result && (result as any).data && (result as any).data.url) {
        return (result as any).data.url;
      }
      console.warn("上传返回格式异常:", result);
      return base64Data;
    } catch (err) {
      console.error("图片上传失败:", err);
      // 上传失败时返回原 base64
      return base64Data;
    }
  };

  // 下载远程图片（通过后端代理，绕过 CORS）
  const downloadImage = async (url: string): Promise<string> => {
    try {
      setUploadProgress(`正在下载图片: ${url.substring(0, 50)}...`);
      const result = await request('/admin/fetch-image', {
        params: { url },
      });
      // 返回本地化后的URL
      if (result && (result as any).url) {
        return (result as any).url;
      }
      console.warn("图片下载返回异常:", result);
      return url;
    } catch (err) {
      console.error("下载图片失败:", err);
      return url; // 失败时保留原 URL
    }
  };

  // 处理粘贴事件 - 支持图文一起粘贴
  const handlePaste = useCallback(async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    // 检查是否有图片
    const items = clipboardData.items;
    let hasImages = false;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith("image/")) {
        hasImages = true;
        break;
      }
    }

    if (hasImages) {
      // 处理直接粘贴的图片文件
      e.preventDefault();
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith("image/")) {
          if (images.length >= 6) {
            setError("最多上传6张图片");
            setTimeout(() => setError(""), 3000);
            break;
          }
          const file = items[i].getAsFile();
          if (file) {
            const reader = new FileReader();
            reader.onload = async (event) => {
              const base64 = event.target?.result as string;
              // 直接添加到图片列表（由用户决定是否上传）
              setImages((prev) => [...prev, base64]);
            };
            reader.readAsDataURL(file);
          }
        }
      }
      return;
    }

    // 检查 HTML 内容中的图片（图文一起复制的情况）
    const html = clipboardData.getData("text/html");
    if (html) {
      const imgRegex = /<img[^>]+src=["']([^"']+)["']/gi;
      const matches = [...html.matchAll(imgRegex)];
      
      if (matches.length > 0) {
        e.preventDefault();
        
        // 1. 获取纯文本内容
        let plainText = clipboardData.getData("text/plain");
        
        // 2. 过滤掉图片文件名（如 image.png, img_xxx.jpg 等）
        // 图片文件名通常是单独一行，且包含扩展名
        const lines = plainText.split('\n');
        const filteredLines = lines.filter(line => {
          const trimmed = line.trim();
          // 跳过类似文件名的行（.png, .jpg, .gif, .webp 等图片扩展名）
          if (/^[\w\-]+\.(png|jpg|jpeg|gif|webp|bmp)(\s*\.png)?$/i.test(trimmed)) {
            return false;
          }
          // 跳过只有空白的行（保留空行以便分段）
          if (trimmed === '') return false;
          return true;
        });
        plainText = filteredLines.join('\n');
        
        // 3. 下载所有远程图片
        const imageUrls = matches.map((m) => m[1]).filter((url) => url.startsWith("http"));
        
        if (images.length + imageUrls.length > 6) {
          setError(`最多上传6张图片，当前已选择${images.length}张`);
          setTimeout(() => setError(""), 3000);
          // 即使超出限制，仍粘贴文字内容
          setFormData((prev) => ({
            ...prev,
            content: prev.content + plainText,
          }));
          return;
        }

        // 下载图片并添加到图片列表
        const downloadedImages: string[] = [];
        for (const url of imageUrls) {
          const downloadedImage = await downloadImage(url);
          downloadedImages.push(downloadedImage);
          setImages((prev) => [...prev, downloadedImage]);
        }

        // 4. 粘贴文字内容到详情
        setFormData((prev) => ({
          ...prev,
          content: prev.content + plainText,
        }));
        
        // 4. 显示提示
        if (downloadedImages.length > 0) {
          setTimeout(() => {
            setError("");
          }, 3000);
        }
      }
    }
    // 如果是纯文本，让浏览器正常处理
  }, [images.length]);

  // 处理图片上传
  const handleImageUpload = () => {
    if (images.length >= 6) {
      setError("最多上传6张图片");
      setTimeout(() => setError(""), 3000);
      return;
    }
    
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = true;
    input.onchange = (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (!files) return;
      
      Array.from(files).forEach((file) => {
        if (images.length >= 6) return;
        
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64 = e.target?.result as string;
          setImages((prev) => [...prev, base64]);
        };
        reader.readAsDataURL(file);
      });
    };
    input.click();
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  // 从详情内容中提取图片URL
  const extractImagesFromContent = (content: string): string[] => {
    const imgRegex = /<img[^>]+src=["']([^"']+)["']/gi;
    const matches = [...content.matchAll(imgRegex)];
    return matches.map((m) => m[1]);
  };

  // 提交表单
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    if (selectedCategoryIds.length === 0) {
      setError("请至少选择一个分类");
      return;
    }
    if (!formData.title.trim()) {
      setError("请输入标题");
      return;
    }
    if (!formData.content.trim()) {
      setError("请输入详情内容");
      return;
    }

    setIsSubmitting(true);
    setUploadProgress("正在上传图片...");
    
    try {
      // 如果有 base64 图片，先上传到服务器
      const finalImages: string[] = [];
      
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        setUploadProgress(`正在上传图片 ${i + 1}/${images.length}...`);
        
        if (img.startsWith('data:')) {
          const uploadedUrl = await uploadImage(img);
          finalImages.push(uploadedUrl);
        } else {
          finalImages.push(img);
        }
      }

      setUploadProgress("正在保存信息...");
      
      await adminCreateListing({
        category_ids: selectedCategoryIds,
        title: formData.title,
        content: formData.content,
        images: finalImages,
        company_name: formData.companyName || undefined,
        contact_name: formData.contactName || undefined,
        contact_phone: formData.contactPhone || undefined,
        contact_wechat: formData.contactWechat || undefined,
        status: formData.status,
      });
      
      setSuccess(true);
    } catch (err) {
      console.error("发布失败:", err);
      setError("发布失败，请重试");
    } finally {
      setIsSubmitting(false);
      setUploadProgress("");
    }
  };

  if (success) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-50 mb-4">
            <i className="ri-check-line text-3xl text-green-500" />
          </div>
          <h2 className="text-lg font-bold text-gray-800 mb-2">发布成功！</h2>
          <p className="text-sm text-gray-500 mb-6">信息已成功发布到前台</p>
          <div className="flex gap-3">
            <button
              onClick={() => {
                setSuccess(false);
                setSelectedCategoryIds([]);
                setFormData({
                  title: "",
                  content: "",
                  companyName: "",
                  contactName: "",
                  contactPhone: "",
                  contactWechat: "",
                  status: "published",
                });
                setImages([]);
              }}
              className="px-5 py-2.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 text-sm cursor-pointer"
            >
              继续发布
            </button>
            <button
              onClick={() => navigate("/admin/listings")}
              className="px-5 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm cursor-pointer"
            >
              查看信息列表
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div>
          <h1 className="text-xl font-bold text-gray-800">发布信息</h1>
          <p className="text-sm text-gray-500 mt-1">以管理员身份发布服务信息</p>
        </div>

        {error && (
          <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg">{error}</div>
        )}

        <form onSubmit={handleSubmit}>
          {/* 左右两栏布局 */}
          <div className="grid grid-cols-2 gap-6">
            {/* 左侧 - 基本信息 */}
            <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-5">
              <h2 className="text-base font-semibold text-gray-800 border-b border-gray-100 pb-3">
                <i className="ri-file-list-3-line mr-2 text-orange-500" />
                基本信息
              </h2>

              {/* 分类标签选择 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  选择分类 <span className="text-red-500">*</span>
                  <span className="text-gray-400 font-normal ml-2">(最多选5个)</span>
                </label>
                {isLoadingCategories ? (
                  <div className="text-sm text-gray-400">加载中...</div>
                ) : categories.length === 0 ? (
                  <div className="text-sm text-gray-400">暂无可用分类</div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => toggleCategory(cat.id)}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                          selectedCategoryIds.includes(cat.id)
                            ? "bg-orange-500 text-white border-orange-500"
                            : "bg-gray-50 text-gray-600 border-gray-200 hover:border-orange-300 hover:bg-orange-50"
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                )}
                {selectedCategoryIds.length > 0 && (
                  <p className="text-xs text-orange-500 mt-2">
                    已选择: {selectedCategoryIds.length} 个分类
                  </p>
                )}
              </div>

              {/* 标题 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  信息标题 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="例如：专业企业官网定制开发"
                  maxLength={100}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-orange-400"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{formData.title.length}/100</p>
              </div>

              {/* 公司 + 联系人 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">公司名称</label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="请输入公司名称"
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-orange-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">联系人</label>
                  <input
                    type="text"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    placeholder="请输入联系人姓名"
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-orange-400"
                  />
                </div>
              </div>

              {/* 电话 + 微信 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">联系电话</label>
                  <input
                    type="tel"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleChange}
                    placeholder="请输入联系电话"
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-orange-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">微信号</label>
                  <input
                    type="text"
                    name="contactWechat"
                    value={formData.contactWechat}
                    onChange={handleChange}
                    placeholder="请输入微信号"
                    className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-orange-400"
                  />
                </div>
              </div>

              {/* 发布状态 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">发布状态</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-orange-400"
                >
                  <option value="published">直接发布</option>
                  <option value="pending">待审核</option>
                  <option value="offline">草稿（不展示）</option>
                </select>
              </div>
            </div>

            {/* 右侧 - 详情内容和图片 */}
            <div className="space-y-5">
              {/* 详情内容 */}
              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-base font-semibold text-gray-800">
                    <i className="ri-file-text-line mr-2 text-orange-500" />
                    详情内容 <span className="text-red-500">*</span>
                  </h2>
                  <span className="text-xs text-gray-400">支持粘贴图文</span>
                </div>
                <textarea
                  ref={contentRef}
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  onPaste={handlePaste}
                  placeholder="详细描述服务内容、优势、价格等信息...

💡 提示：从其他网站复制图文内容粘贴到这里，图片会自动下载并添加到下方图片列表中"
                  rows={12}
                  maxLength={5000}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-orange-400 resize-none"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{formData.content.length}/5000</p>
              </div>

              {/* 图片上传 */}
              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-base font-semibold text-gray-800">
                    <i className="ri-image-line mr-2 text-orange-500" />
                    图片附件
                  </h2>
                  <span className="text-xs text-gray-400">
                    {images.length}/6 张
                    {images.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setImages([])}
                        className="ml-2 text-orange-500 hover:text-orange-600"
                      >
                        清空
                      </button>
                    )}
                  </span>
                </div>
                
                {/* 从内容提取的图片提示 */}
                {extractImagesFromContent(formData.content).length > 0 && (
                  <div className="mb-3 p-2 bg-blue-50 rounded-lg text-xs text-blue-600">
                    <i className="ri-information-line mr-1" />
                    检测到详情内容中包含 {extractImagesFromContent(formData.content).length} 张图片
                  </div>
                )}
                
                <DraggableImageGrid
                  images={images}
                  onReorder={setImages}
                  onRemove={removeImage}
                  onAdd={handleImageUpload}
                />
                <p className="text-xs text-gray-400 mt-3">
                  <i className="ri-information-line mr-1" />
                  支持粘贴、点击上传或拖拽排序，第一张为封面图，最多6张
                </p>
              </div>
            </div>
          </div>

          {/* 底部按钮 */}
          <div className="flex gap-3 pt-6 mt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={() => navigate("/admin/listings")}
              className="px-6 py-2.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 text-sm cursor-pointer"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 py-2.5 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 text-sm cursor-pointer"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />
                  {uploadProgress || "发布中..."}
                </span>
              ) : (
                "立即发布"
              )}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}
